// In dev this is "/" — src/setupProxy.js forwards /api to staging so the browser sees a
// single origin, which is what makes the SameSite=Lax session cookie attach on localhost.
export const BASE_URL = process.env.REACT_APP_API_URL;

// Absolute backend origin. BASE_URL is relative in dev, so anything that must be a real
// public URL (webhooks, third-party redirects) has to use this instead.
export const API_ORIGIN = BASE_URL?.startsWith("http")
  ? BASE_URL.replace(/\/$/, "")
  : "https://staging.backend-app.usedeex.com"; // dev proxy target

// Use this rather than BASE_URL?.includes("staging") — BASE_URL is "/" in dev, which would
// otherwise read as production and e.g. point users at the live business page.
export const IS_STAGING = API_ORIGIN.includes("staging");

// NB: the matching callback URL is also registered in the Smile Identity dashboard —
// changing this alone is not enough.
export const SMILE_URL = `${API_ORIGIN.replace(
  /^https?:\/\//,
  ""
)}/webhooks/smileid`;

/**
 * Sent with signin / complete-signin so the backend knows which client is
 * authenticating. The merchant backend role is still being built — until it
 * ships, merchant authenticates as "user" like the PWA, desk and mobile apps
 * (admin uses "admin"). Change this one value when the merchant role is live.
 */
export const ROLE_PASS = "user";

export const API = {
  // auth
  login: `${BASE_URL}api/auth/signin`,
  signup: `${BASE_URL}api/auth/signup`,
  forgotPassword: `${BASE_URL}api/auth/forgot-password`,
  resetPassword: `${BASE_URL}api/auth/reset-password`,
  createPassword: `${BASE_URL}api/auth/create-password`,
  verifyEmail: `${BASE_URL}api/users/me/complete-email-verification`,
  resendOTP: `${BASE_URL}api/users/me/resend-otp`,
  createPin: `${BASE_URL}api/auth/create-pin`,
  changePin: `${BASE_URL}api/auth/change-pin`,
  completeLogin: `${BASE_URL}api/auth/complete-signin`,
  // Cookie auth: the session is httpOnly, so only the server can end it.
  logout: `${BASE_URL}api/auth/logout`,

  // user
  getUser: `${BASE_URL}api/users/me`,
  /** PATCH — same endpoint the PWA uses to update profile fields. */
  profile: `${BASE_URL}api/users/me`,
  verifyKyc:`${BASE_URL}api/users/me/kyc/smile-id-bvn`,
  survey: `${BASE_URL}api/users/me/survey`,
  phone: `${BASE_URL}api/users/me/phone`,
  getOldBanks: `${BASE_URL}api/banks`,
  getBanks: `${BASE_URL}api/banks/palmpay`,
  makeDefaultBank: (bussinessID:string) =>
    `${BASE_URL}api/businesses/${bussinessID}/banks/default`,
  deleteBank: (bussinessID:string) =>
    `${BASE_URL}api/businesses/${bussinessID}/banks`,
   verifyAccountNumber: (accountNumber: string, bankCode: string) =>
    `${BASE_URL}api/banks/account-name/palmpay?accountNumber=${accountNumber}&bankCode=${bankCode}`,
  saveBusinessAccountDetails: (bussinessID:string) =>  `${BASE_URL}api/businesses/${bussinessID}/bank-details`,
  
  getSummary: `${BASE_URL}api/users/me/summary`,
  changePassword: `${BASE_URL}api/users/me/password`,
  requestOTP: `${BASE_URL}api/users/me/request-otp`,
  checkUsername: (username: string) =>
    `${BASE_URL}api/users/username/${username}/availability`,
  addUsername: `${BASE_URL}api/users/me/username`,
  savePushToken: `${BASE_URL}api/users/me/push-tokens`,
  kycLevel2: `${BASE_URL}api/users/me/kyc/bvn`,
  verifyKycLevel2: `${BASE_URL}api/users/me/kyc/bvn/verify-otp`,

  // notification
  getNotifications: (page: number) =>
    `${BASE_URL}api/notifications/in-app?page=${page}&limit=10&product=MERCHANT`,

 

  //settings
  toggle2Fa: (active: boolean) =>
    `${BASE_URL}api/users/me/account/settings/2fa/${
      active ? "activate" : "deactivate"
    }`,
  requestQrcode: `${BASE_URL}api/users/me/account/settings/2fa-auth-app/request-qrcode`,
  activateGoogleAuth: `${BASE_URL}api/users/me/account/settings/2fa-auth-app/activate`,
  deactivateGoogleAuth: `${BASE_URL}api/users/me/account/settings/2fa-auth-app/deactivate`,

  //in app
  getRates: `${BASE_URL}api/rates`,
  getTickers: `${BASE_URL}api/rates/markets/tickers`,

  //deex assets
  requestWallets: `${BASE_URL}api/deex-assets/wallets/request-crypto-wallets`,
  requestNgnWallets: `${BASE_URL}api/deex-assets/wallets/request-fiat-wallets`,
  getWallets: `${BASE_URL}api/deex-assets/wallets`,
  executeSwapping: `${BASE_URL}api/deex-assets/swaps/execute-instant-order`,
  executeSwappingFromNgn: `${BASE_URL}api/deex-assets/swaps/from-ngn`,
  executeSwappingToNgn: `${BASE_URL}api/deex-assets/swaps/to-ngn`,
  requestSwappingQuote: `${BASE_URL}api/deex-assets/swaps/create-instant-order`,
  coinCurrentValue: (coin = "") =>
    `${BASE_URL}api/rates/crypto-values/${coin}`,
  withdrawAsset: `${BASE_URL}api/deex-assets/request-withdrawal`,
  withdrawToDeexAsset: `${BASE_URL}api/deex-assets/request-deex-withdrawal`,
  sellCrypto: `${BASE_URL}api/deex-assets/sell-crypto`,
  requestNairaDeposit: `${BASE_URL}api/deex-assets/request-naira-deposit-bank`,
  requestWithdrawalFee: (network: string, currency: string) =>
    `${BASE_URL}api/deex-assets/withdrawal-fee?network=${network}&currency=${currency}`,

  getDeexTags: (search: string) =>
    `${BASE_URL}api/users/verified?limit=10&page=1&search=${search}`,

  //reward
  getFplManagers: `${BASE_URL}api/tasks/fpl/manager-of-the-week-history`,
  getTasks: `${BASE_URL}api/tasks`,
  redeemPoints: `${BASE_URL}api/tasks/redeem-points`,
  applyTask: (id: string) => `${BASE_URL}api/tasks/${id}/apply`,
  getTradeStreaks: (filter: string) =>
    `${BASE_URL}api/tasks/trade-streaks${filter}`,
  applyTradeStreak: (id: string) =>
    `${BASE_URL}api/tasks/trade-streaks/${id}/claim-reward`,

  //Merchant
  getBusinesses: `${BASE_URL}api/businesses`,
  createBusiness: `${BASE_URL}api/businesses`,
  getBusiness: (id:string, from:string, to:string)=> `${BASE_URL}api/businesses/${id}?from=${from}&to=${to}`,
  /**
   * PATCH — partial detail edits ({ name, email, details: { ... } }).
   * Not yet implemented by the backend; the Business settings tab is built
   * against it and will 404 until it ships.
   */
  updateBusiness: (id:string)=> `${BASE_URL}api/businesses/${id}`,
  /**
   * PUT — registration submission: upgrade a STARTER business or resubmit a
   * rejected one. Body: { bnNumber, identity, proofOfAddress, cac }
   */
  submitBusinessDocuments: (id:string)=> `${BASE_URL}api/businesses/${id}/documents`,
  getPaymentTxn: (referenceId:string, from:string, to:string, limit:number, page:number)=> `${BASE_URL}api/businesses/payment-pages/${referenceId}/deposits?page=${page}&limit=${limit}&from_date=${from}&to_date=${to}`,
  createPaymentLink:(id:string) =>  `${BASE_URL}api/businesses/${id}/payment-pages`,
  // PUT — partial edit of an existing page (name, amount, bank, …). Also how a
  // bank gets attached to older pages created before bank selection existed.
  updatePaymentPage:(id:string, reference:string) =>  `${BASE_URL}api/businesses/${id}/payment-pages/${reference}`,
  getPaymentPages:(id:string, page:number, searchValue="") =>  `${BASE_URL}api/businesses/${id}/payment-pages?limit=10&page=${page}${searchValue?`&search=${searchValue}`:""}`,
  // Unauthenticated twin of getPaymentPages for the customer-facing business
  // link page. Returns only what a customer needs, plus `business` (name+logo).
  getPublicPaymentPages:(id:string, page:number, searchValue="") =>  `${BASE_URL}api/businesses/public/${id}/pages?limit=10&page=${page}${searchValue?`&search=${searchValue}`:""}`,
  getTransaction:(id:string, page:number, from:string, to:string,limit:number ) =>  `${BASE_URL}api/businesses/${id}/transactions?limit=${limit}&page=${page}&from_date=${from}&to_date=${to}`,
  getSinglePaymentPage:(id:string) =>  `${BASE_URL}api/businesses/payment-pages/${id}`,
    requestBusinessImageUpload: `${BASE_URL}api/businesses/request-upload-url`,
  requestDocumentUpload: `${BASE_URL}api/businesses/documents/request-upload-url`,
sendCustomerDeets:(reference: string) =>  `${BASE_URL}api/businesses/payment-pages/${reference}/optin`,

  // branches
  createBranch: (businessId: string) =>
    `${BASE_URL}api/businesses/${businessId}/branches`,

  // managers
  getManagers: (businessId: string) =>
    `${BASE_URL}api/businesses/${businessId}/managers`,
  inviteManager: (businessId: string) =>
    `${BASE_URL}api/businesses/${businessId}/managers/invite`,
  removeManager: (businessId: string, managerUserId: string) =>
    `${BASE_URL}api/businesses/${businessId}/managers/${managerUserId}`,
};